import { bindActionCreators } from '@reduxjs/toolkit';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { trackEquipmentFilterSearch, trackSoftConversionEvent } from '../../actions/tracking';
import ExtendableGroupAuto from '../../components/extendable-group/extendable-group-auto';
import { getItem, getItemOptions } from '../../selectors/item';
import { softConversionEvents } from '../../shared/tracking';
import Alert from '../../styled-components/components/Alert';
import Content from '../../styled-components/components/Content';
import { DataBox } from '../../styled-components/components/DataBox';
import { H3 } from '../../styled-components/components/Heading';
import InputSearch from '../../styled-components/components/InputSearch';
import { StackMedium } from '../../styled-components/components/Stack';
import { size } from '../../styled-components/layout/helpers';

const InputSearchWrapper = styled.div.withConfig({ componentId: 'sc-d1hx05-0' })`
    margin-bottom: ${size(2)};
`;

const DataBoxList = styled.div.withConfig({ componentId: 'sc-d1hx05-1' })`
    --_gap: ${size(1)};
    --_column-count: 2;
    --_min-width: ${size(35)};

    // Calculcated values
    --_gap-count: calc(var(--_column-count) - 1);
    --_total-gap-width: calc(var(--_gap-count) * var(--_gap));
    --_max-width: calc((100% - var(--_total-gap-width)) / var(--_column-count));

    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(max(min(var(--_min-width), 100%), var(--_max-width)), 1fr));
    gap: ${size(1)};
`;

const Equipment = () => {
  const { t } = useTranslation(['item']);
  const ref = useRef(-1);
  const dispatch = useDispatch();
  const item = useSelector(getItem);
  const options = useSelector(getItemOptions);
  const actions = bindActionCreators(
    {
      trackSoftConversionEvent,
      trackEquipmentFilterSearch,
    },
    dispatch,
  );

  if (!options || !options.length || !item) return null;

  const contentRef = useRef<HTMLDivElement | null>(null);
  const [search, setSearch] = useState('');

  const onToggle = () =>
    actions.trackSoftConversionEvent(
      item,
      softConversionEvents.SOFT_CONVERSION_SHOW_VEHICLE_OPTIONS,
    );

  const filteredOptions = options.filter((option) =>
    option.toLowerCase().includes(search.toLowerCase()),
  );

  const onSearch = (event: React.ChangeEvent<HTMLInputElement>) => setSearch(event.target.value);

  // Track filter search
  useEffect(() => {
    clearTimeout(ref.current);
    ref.current = window.setTimeout(() => {
      if (search) {
        actions.trackEquipmentFilterSearch(item, {
          search,
        });
      }
    }, 1000);
  }, [search]);

  return (
    <div className="item-section">
      <StackMedium>
        <H3 as="h2" $margin="small">
          {t('Equipment', { ns: 'item' })}
        </H3>
        <Content>
          <p>{t('Information about the car that the dealer supplemented with.', { ns: 'item' })}</p>
        </Content>
      </StackMedium>
      <StackMedium>
        <InputSearchWrapper>
          <InputSearch
            value={search}
            onChange={onSearch}
            placeholder={t('equipmentSearch.placeholder', { ns: 'item' })}
            aria-label={t('equipmentSearch.label', { ns: 'item' })}
          />
        </InputSearchWrapper>
        {!!filteredOptions.length ? (
          <ExtendableGroupAuto
            label={t('viewAllEquipment', { ns: 'item' })}
            onToggle={onToggle}
            childRef={contentRef}
          >
            <DataBoxList ref={contentRef}>
              {filteredOptions.map((option, index) => (
                <DataBox key={`${option}-${index}`}>{option}</DataBox>
              ))}
            </DataBoxList>
          </ExtendableGroupAuto>
        ) : (
          <Alert tone="warning">{t('equipmentSearch.noResults', { ns: 'item' })}</Alert>
        )}
      </StackMedium>
    </div>
  );
};

export default Equipment;
